import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { formatToString } from 'utils/date-format';

import { Stack, Divider } from '@mui/material';
import {
  CreateOutlined,
  DeleteOutlineOutlined,
  HomeOutlined,
  RequestQuoteOutlined,
  ShareOutlined,
  WallpaperOutlined,
} from '@mui/icons-material';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { removePropertyPhoto } from 'store/slices/properties';
import { resetShareList, shareList } from 'store/slices/files';
import { IllustrationProperties } from 'components/shared/illustrations/IllustrationProperties';
import { usePropertyDrawers } from 'hooks/usePropertyDrawers';
import { useProperty } from 'hooks/useProperty';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';

export const PropertyDetails = (props) => {
  const { close } = props;

  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();

  const { enqueueSnackbar } = useSnackbar();
  const { openDrawerEditProperty, openDrawerShareProperty } = usePropertyDrawers();
  const { handleDeleteProperty, handleCreateQuote } = useProperty();

  const selectedProperty = useSelector((state) => state.properties.selectedProperty);
  const currentUserId = useSelector((state) => state.auth.currentUser?.id);
  const isCapableOfFileModification = useMemo(
    () => selectedProperty && selectedProperty?.access && selectedProperty?.access?.includes('Write'),
    [selectedProperty]
  );
  const propertyOwner = useMemo(() => {
    if (selectedProperty && selectedProperty?.user_id === currentUserId) {
      return 'Me';
    }
    return '';
  }, [selectedProperty, currentUserId]);
  const hasPropertyPhoto = useMemo(() => selectedProperty?.preview_file_id, [selectedProperty]);

  /* shared users */
  const sharedUserList = useSelector((state) => state.files.shareList.value);

  const handleRemovePropertyPhoto = (selectedProperty) => {
    dispatchAction(removePropertyPhoto({ id: selectedProperty.id }), {
      onSuccess: () => {
        enqueueSnackbar('Property photo has been removed');
      },
    });
  };

  useEffect(() => {
    dispatch(resetShareList());
    if (selectedProperty?.id) {
      dispatch(shareList({ fileId: selectedProperty?.id }));
    }
  }, [dispatch, selectedProperty]);

  return (
    <DrawerContent>
      <DrawerContent.Header title={selectedProperty?.name} />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <EntityFileDetails>
            {!selectedProperty && (
              <EntityFileDetails.EmptyFile
                illustration={
                  <IllustrationProperties
                    width={220}
                    height={180}
                  />
                }
                title="Select a property to view details"
              />
            )}
            {selectedProperty && (
              <>
                <EntityFileDetails.FileType
                  file={selectedProperty}
                  fileIcon={<HomeOutlined sx={{ fontSize: '100px' }} />}
                />
                {sharedUserList.length > 0 && (
                  <>
                    <EntityFileDetails.PeopleWithAccess users={sharedUserList} />
                    <Divider />
                  </>
                )}
                <EntityFileDetails.Details title="Details">
                  {selectedProperty?.fileType && (
                    <EntityFileDetails.DetailsItem
                      title="Type"
                      subtitle={selectedProperty?.fileType}
                    />
                  )}
                  {propertyOwner && (
                    <EntityFileDetails.DetailsItem
                      title="Owner"
                      subtitle={propertyOwner}
                    />
                  )}
                  {selectedProperty?.last_modified && (
                    <EntityFileDetails.DetailsItem
                      title="Modified"
                      subtitle={formatToString(selectedProperty?.last_modified)}
                    />
                  )}
                  {selectedProperty?.createdOn && (
                    <EntityFileDetails.DetailsItem
                      title="Created"
                      subtitle={formatToString(selectedProperty?.createdOn)}
                    />
                  )}
                </EntityFileDetails.Details>
                <Divider />
                {isCapableOfFileModification && (
                  <Stack
                    direction="column"
                    spacing={1.5}
                  >
                    {hasPropertyPhoto && (
                      <EntityFileDetails.ActionButton
                        title="Remove property photo"
                        icon={<WallpaperOutlined sx={{ fontSize: '22px' }} />}
                        action={() => handleRemovePropertyPhoto(selectedProperty)}
                      />
                    )}
                    <EntityFileDetails.ActionButton
                      title="Edit Property"
                      icon={<CreateOutlined sx={{ fontSize: '22px' }} />}
                      action={openDrawerEditProperty}
                    />
                    <EntityFileDetails.ActionButton
                      title="Create Quote"
                      icon={<RequestQuoteOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleCreateQuote(selectedProperty)}
                    />
                    {propertyOwner && (
                      <EntityFileDetails.ActionButton
                        title="Share"
                        icon={<ShareOutlined sx={{ fontSize: '22px' }} />}
                        action={openDrawerShareProperty}
                      />
                    )}
                    <EntityFileDetails.ActionButton
                      title="Delete"
                      icon={<DeleteOutlineOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDeleteProperty(selectedProperty, close)}
                    />
                  </Stack>
                )}
              </>
            )}
          </EntityFileDetails>
        </DrawerContent.Content>
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
